
import {
  computed,
  ComputedRef,
  defineComponent,
  nextTick,
  onBeforeMount,
  onMounted,
  Ref,
  ref,
  onBeforeUnmount,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import {
  hideLeftToolbar,
  resetActiveSection,
  setActiveSection, setPerfectSizeForImage,
  showColumnWidgetModal,
  removeColumnWidgetPlaceholders,
  addNewForm,
  updateTemplatePreviewDisplay,
  showElementsModal,
  TemplateEditorState as state, togglePanel, updateSectionsInState, markTemplateAsEdited,
  hasEmailWidgetInFunnel,
  hasPhoneWidgetInFunnel,
  countPasswordWidgetsInFunnel,
  hasGameWidgetInFunnel,
  hasDisclosureWidgetInTemplate,
  hasEmailOrPhoneWidgetInTemplate,
  formExistsInTemplate,
  countConsentCheckboxesInTemplate,
  getAutoDisclosureChannels,
  updateDisclosureAfterWidgetAddition,
  syncGameFunnelSteps,
  toggleWidgetsPanelSection,
} from '@/composables/template-editor/TemplateEditor';
import {
  HistoryType,
  TemplateStructureEnum,
  WidgetTypeEnum,
  SelectedSyncElement,
} from '@/types';
import {
  getTemplateIframeDocument,
  getTemplateParentTypeByType,
  isDisplayTemplate,
  isEmailTemplate,
  isPopupTemplate,
  isEmbedTemplate,
  isFacebookTemplate,
  getSectionParent,
  clearPlaceholder,
  isV4Template,
} from '@/components/template-builder/utils/helpers';
import { getWidgetStructures } from '@/components/template-builder/utils/raw-html-templates';
import { rawWidgetIntegration } from '@/components/template-builder/utils/raw-widget-integration';
import { useIntegrationWidgets } from '@/composables/widgets/useIntegrationWidgets';
import { pickLocalizedText } from '@/utils/localized-text';
import type { WidgetCatalogEntry, WidgetLocalizedText, WidgetTarget } from '@/types/integrations';
import { refreshSmartProductList } from '@/components/template-builder/callbacks';
import { checkIfSavePointNeeded, createHistory } from '@/composables/template-editor/History';
import {
  executeWhenDragComplete,
  pruneOrphanGlobalSortableInstances,
  resetListeners,
  sortableGlobalInstances,
  trackMousePosition,
  stopScrolling,
  handleDrag,
  applySyncSectionDropPolicy,
} from '@/components/template-builder/utils/listeners';
import Sortable from 'sortablejs';
import {
  ANIMATION_SPEED,
  BUILDER_SORTABLE_PLACEHOLDER_HOVER_CLASS,
  COLUMN_WITH_PLACEHOLDER_CLASS,
  WIDGET_DROPPABLE_CLASS,
  WIDGETS_PLACEHOLDER_IDENTIFIER,
  WIDGET_IMPORT_ZIP_PLACEHOLDER_IDENTIFIER,
  BUILDER_ELEMENT_HOVER_CLASS,
  BUILDER_ELEMENT_PLACEHOLDER,
  BUILDER_DRAG_ACTIVE_CLASS,
  TEMPLATE_SYNC_ELEMENT_IDENTIFIER,
  TEMPLATE_PART_ELEMENT_PART_TYPE,
} from '@/components/template-builder/utils/constants';
import { addOrUpdateDynamicStyle, computeActiveSection } from '@/components/template-builder/utils/parser';
import { initWidget } from '@/components/template-builder/utils/widget-init-registry';
import { getParents, showToast } from '@/helpers';
// eslint-disable-next-line import/no-cycle
import { MARKETPLACE_ENABLED } from '@/router';
import EmbedPosition from '@/components/template-builder/fields/EmbedPosition.vue';
import { TemplateTypeEnum } from '@/composables/shop/Templates';
import Panel from 'primevue/panel';
import { GetShopsConfigurationList } from '@/composables/shop/ShopsConfiguration';
import {
  UserState,
  hasAccessToFeatures,
} from '@/composables/User';
import { unserialize } from 'php-serialize';
import { useStore } from '@/store';

import {
  generateTranslations,
} from '@/components/template-builder/utils/translate';
import { injectWidgetCss } from '@/components/template-builder/utils/widget-css-manager';

import WidgetsPanelButton from '@/components/template-builder/panels/WidgetsPanelButton.vue';
import AppWidgetButton, { AppWidget } from '@/components/template-builder/panels/AppWidgetButton.vue';
import IntegrationWidgetsBrowserModal from '@/components/template-builder/panels/IntegrationWidgetsBrowserModal.vue';

import { useConfirm } from 'primevue/useconfirm';

interface WidgetButton {
  type: WidgetTypeEnum;
  iconClass?: string;
  iconImage?: string;
  label: string | (() => string);
  group: TemplateStructureEnum;
  hide?: Function;
  disabled?: Function;
  disabledTooltip?: string;
}

export default defineComponent({
  name: 'WidgetsPanel',

  components: {
    EmbedPosition,
    Panel,
    WidgetsPanelButton,
    AppWidgetButton,
    IntegrationWidgetsBrowserModal,
  },

  props: {
    isClosable: {
      type: Boolean,
      required: false,
      default: true,
    },
  },

  setup() {
    const { t } = useI18n();
    const store = useStore();
    const confirm = useConfirm();

    const selectedSyncElement = computed<SelectedSyncElement>(() => store.getters['liveEditor/getSelectedSyncElement']);
    const allWidgetsMap: Ref<Array<WidgetButton>> = ref([
      {
        type: WidgetTypeEnum.TITLE,
        iconClass: 'fal fa-heading',
        label: t('templateBuilder.widgets.title'),
        group: TemplateStructureEnum.WIDGET,
      },
      {
        type: WidgetTypeEnum.TEXT,
        iconClass: 'fal fa-paragraph',
        label: isFacebookTemplate(state.template?.type) ? t('templateBuilder.widgets.fbTextButtons.widgetName') : t('templateBuilder.widgets.text'),
        group: TemplateStructureEnum.WIDGET,
      },
      {
        type: WidgetTypeEnum.IMAGE,
        iconClass: 'fal fa-images',
        label: isFacebookTemplate(state.template?.type) ? t('templateBuilder.widgets.fbImageButtons') : t('templateBuilder.widgets.image'),
        group: TemplateStructureEnum.WIDGET,
      },
      {
        type: WidgetTypeEnum.SOCIAL_MEDIA,
        iconClass: 'fab fa-twitter', // alternative icon
        label: t('templateBuilder.widgets.socialMedia'),
        group: TemplateStructureEnum.WIDGET,
      },
      {
        type: WidgetTypeEnum.SHARE_SOCIAL_MEDIA,
        iconClass: 'fal fa-share-alt',
        label: t('templateBuilder.widgets.shareSocialMedia'),
        group: TemplateStructureEnum.WIDGET,
      },
      {
        type: WidgetTypeEnum.CODE,
        iconClass: 'fal fa-code',
        label: t('templateBuilder.widgets.code'),
        group: TemplateStructureEnum.WIDGET,
      },
      {
        type: WidgetTypeEnum.JAVASCRIPT,
        iconClass: 'fal fa-brackets-curly',
        label: t('templateBuilder.widgets.javascript'),
        group: TemplateStructureEnum.WIDGET,
      },
      {
        type: WidgetTypeEnum.BUTTON,
        iconClass: 'fal fa-rectangle-wide', // alternative icon
        label: t('templateBuilder.widgets.button'),
        group: TemplateStructureEnum.WIDGET,
      },
      {
        type: WidgetTypeEnum.SEPARATOR,
        iconClass: 'fal fa-horizontal-rule',
        label: t('templateBuilder.widgets.separator'),
        group: TemplateStructureEnum.WIDGET,
      },
      {
        type: WidgetTypeEnum.SPACER,
        iconClass: 'fal fa-arrows-alt-v',
        label: t('templateBuilder.widgets.spacer'),
        group: TemplateStructureEnum.WIDGET,
      },
      {
        type: WidgetTypeEnum.SMART_PRODUCT_LIST,
        iconClass: 'fal fa-cart-plus',
        label: t('templateBuilder.widgets.smartProductList'),
        group: TemplateStructureEnum.WIDGET,
      },
      {
        type: WidgetTypeEnum.VOUCHER,
        iconClass: 'fal fa-tags',
        label: t('templateBuilder.widgets.voucher'),
        group: TemplateStructureEnum.WIDGET,
      },
      {
        type: WidgetTypeEnum.COUNTDOWN,
        iconClass: 'fal fa-hourglass-half',
        label: t('templateBuilder.widgets.countdown'),
        group: TemplateStructureEnum.WIDGET,
      },
      {
        type: WidgetTypeEnum.MENU,
        iconClass: 'fal fa-bars',
        label: t('templateBuilder.widgets.menu'),
        group: TemplateStructureEnum.WIDGET,
      },
      {
        type: WidgetTypeEnum.CREATE_ACCOUNT,
        iconClass: 'fal fa-user-circle',
        label: t('templateBuilder.widgets.createCustomerAccount'),
        // Widgets form V3 : visibles UNIQUEMENT en ancienne version (V3) sur modeles embed/popin,
        // et masques si un form est deja present dans le modele.
        hide: () => formExistsInTemplate() || isV4Template() || (state.template?.type !== TemplateTypeEnum.EMBED && state.template?.type !== TemplateTypeEnum.POPUP),
        group: TemplateStructureEnum.WIDGET,
      },
      {
        type: WidgetTypeEnum.NEWSLETTER_SUBSCRIPTION,
        iconClass: 'fal fa-user-plus',
        label: t('templateBuilder.widgets.newsletterSubscription'),
        hide: () => formExistsInTemplate() || isV4Template() || (state.template?.type !== TemplateTypeEnum.EMBED && state.template?.type !== TemplateTypeEnum.POPUP),
        group: TemplateStructureEnum.WIDGET,
      },
      {
        type: WidgetTypeEnum.NEWSLETTER_UNSUBSCRIPTION,
        iconClass: 'fal fa-user-times',
        label: t('templateBuilder.widgets.newsletterUnsubscription'),
        hide: () => formExistsInTemplate() || isV4Template() || (state.template?.type !== TemplateTypeEnum.EMBED && state.template?.type !== TemplateTypeEnum.POPUP),
        group: TemplateStructureEnum.WIDGET,
      },
      {
        type: WidgetTypeEnum.IMPORT_ZIP,
        iconClass: 'fal fa-file-arrow-up',
        label: t('templateBuilder.widgets.importZip'),
        group: TemplateStructureEnum.WIDGET,
      },
    ]);

    const allStructureWidgetsMap: Ref<Array<WidgetButton>> = ref([
      {
        type: WidgetTypeEnum.COLUMN,
        iconClass: 'fal fa-columns',
        label: t('templateBuilder.widgets.column'),
        group: TemplateStructureEnum.LINE,
      },
      {
        type: WidgetTypeEnum.SECTION,
        iconClass: 'fal fa-pager',
        label: t('templateBuilder.widgets.section'),
        hide: () => isDisplayTemplate(state.template?.type) || selectedSyncElement.value,
        group: TemplateStructureEnum.SECTION,
      },
    ]);
    const allFormWidgetsMap: Array<WidgetButton> = [
      {
        type: WidgetTypeEnum.INPUT_TEXT,
        iconClass: 'fal fa-input-pipe',
        label: t('templateBuilder.formWidgets.inputText'),
        group: TemplateStructureEnum.WIDGET,
      },
      {
        type: WidgetTypeEnum.INPUT_EMAIL,
        iconClass: 'fal fa-envelope',
        label: t('templateBuilder.formWidgets.inputEmail'),
        group: TemplateStructureEnum.WIDGET,
        disabled: () => hasEmailWidgetInFunnel(),
        disabledTooltip: t('templateBuilder.widgetTooltip.emailWidgetLimit'),
      },
      {
        type: WidgetTypeEnum.INPUT_PHONE,
        iconClass: 'fal fa-phone',
        label: t('templateBuilder.formWidgets.inputPhone'),
        group: TemplateStructureEnum.WIDGET,
        disabled: () => hasPhoneWidgetInFunnel(),
        disabledTooltip: t('templateBuilder.widgetTooltip.phoneWidgetLimit'),
      },
      {
        type: WidgetTypeEnum.INPUT_PASSWORD,
        iconClass: 'fal fa-lock',
        label: () => {
          const passwordCount = countPasswordWidgetsInFunnel();
          return passwordCount === 0
            ? t('templateBuilder.formWidgets.inputPassword')
            : t('templateBuilder.formWidgets.inputPasswordConfirmation');
        },

        group: TemplateStructureEnum.WIDGET,
        disabled: () => countPasswordWidgetsInFunnel() >= 2,
        disabledTooltip: t('templateBuilder.widgetTooltip.passwordWidgetLimit'),
      },
      {
        type: WidgetTypeEnum.TEXTAREA,
        iconClass: 'fal fa-align-left',
        label: t('templateBuilder.formWidgets.textarea'),
        group: TemplateStructureEnum.WIDGET,
      },
      {
        type: WidgetTypeEnum.INPUT_DATE,
        iconClass: 'fal fa-calendar',
        label: t('templateBuilder.formWidgets.inputDate'),
        group: TemplateStructureEnum.WIDGET,
      },
      {
        type: WidgetTypeEnum.INPUT_CHECKBOX,
        iconClass: 'fal fa-ballot-check',
        label: t('templateBuilder.formWidgets.inputCheckbox'),
        group: TemplateStructureEnum.WIDGET,
      },
      {
        type: WidgetTypeEnum.INPUT_RADIO,
        iconClass: 'far fa-circle-dot',
        label: t('templateBuilder.formWidgets.inputRadio'),
        group: TemplateStructureEnum.WIDGET,
      },
      {
        type: WidgetTypeEnum.INPUT_SELECT,
        iconClass: 'far fa-square-chevron-down',
        label: t('templateBuilder.formWidgets.inputSelect'),
        group: TemplateStructureEnum.WIDGET,
      },
      {
        type: WidgetTypeEnum.CONSENT_CHECKBOX,
        iconClass: 'fas fa-shield-check',
        label: t('templateBuilder.formWidgets.consentCheckbox'),
        group: TemplateStructureEnum.WIDGET,
        disabled: () => countConsentCheckboxesInTemplate() >= 3,
        disabledTooltip: t('templateBuilder.widgetTooltip.consentCheckboxLimit'),
      },
      {
        type: WidgetTypeEnum.DISCLOSURE,
        iconClass: 'fas fa-file-contract',
        label: t('templateBuilder.formWidgets.disclosure'),
        group: TemplateStructureEnum.WIDGET,
        disabled: () => hasDisclosureWidgetInTemplate() || !hasEmailOrPhoneWidgetInTemplate(),
        disabledTooltip: hasDisclosureWidgetInTemplate()
          ? t('templateBuilder.widgetTooltip.disclosureAlreadyExists')
          : t('templateBuilder.widgetTooltip.disclosureRequiresEmailOrPhone'),
      },
    ];

    const allGameWidgetsMap: Array<WidgetButton> = [
      {
        type: WidgetTypeEnum.GAME_WHEEL,
        iconClass: 'fal fa-circle-notch',
        label: t('templateBuilder.gameWidgets.wheel'),
        group: TemplateStructureEnum.WIDGET,
        disabled: () => hasGameWidgetInFunnel(),
        disabledTooltip: t('templateBuilder.widgetTooltip.gameWidgetLimit'),
      },
      {
        type: WidgetTypeEnum.GAME_SLOT_MACHINE,
        iconClass: 'fal fa-slot-machine',
        label: t('templateBuilder.gameWidgets.slotMachine'),
        group: TemplateStructureEnum.WIDGET,
        disabled: () => hasGameWidgetInFunnel(),
        disabledTooltip: t('templateBuilder.widgetTooltip.gameWidgetLimit'),
      },
      {
        type: WidgetTypeEnum.GAME_SCRATCH_CARD,
        iconClass: 'fal fa-ticket',
        label: t('templateBuilder.gameWidgets.scratchCard'),
        group: TemplateStructureEnum.WIDGET,
        disabled: () => hasGameWidgetInFunnel(),
        disabledTooltip: t('templateBuilder.widgetTooltip.gameWidgetLimit'),
      },
    ];

    const templateType = computed(() => state.template?.type);
    const templateParentType: ComputedRef<string> = computed(() => getTemplateParentTypeByType(templateType.value));
    const isEmbed = computed(() => templateType.value === TemplateTypeEnum.EMBED);
    const showGameWidgets = computed(() => isDisplayTemplate(templateType.value));

    // Editor target for integration widgets (catalogue + drop + render):
    // email → email_template, popup → popup, embed → smart_content. null when
    // the current template type has no widget target (sms, push, …).
    const widgetTarget = computed<WidgetTarget | null>(() => {
      if (isEmailTemplate(templateType.value)) return 'email_template';
      if (isPopupTemplate(templateType.value)) return 'popup';
      if (isEmbedTemplate(templateType.value)) return 'smart_content';
      return null;
    });

    const additionalWidgetsConfiguration: Ref<Record<string, any>> = ref({});
    const widgetStructureMap = computed(() => getWidgetStructures(t, additionalWidgetsConfiguration.value, isV4Template())[templateParentType.value]);

    const availableWidgetsButtonsMap: ComputedRef<Array<WidgetButton>> = computed(() => allWidgetsMap.value.filter(
      (button) => Object.keys(widgetStructureMap.value).indexOf(button.type) >= 0 && (!Object.getOwnPropertyDescriptor(button, 'hide') || (button.hide && !button.hide())),
    ));
    const availableStructureWidgetsButtonsMap: ComputedRef<Array<WidgetButton>> = computed(() => allStructureWidgetsMap.value.filter(
      (button) => Object.keys(widgetStructureMap.value).indexOf(button.type) >= 0 && (!Object.getOwnPropertyDescriptor(button, 'hide') || (button.hide && !button.hide())),
    ));
    const availableFormWidgetsButtonsMap: ComputedRef<Array<WidgetButton>> = computed(() => allFormWidgetsMap.filter(
      (button) => Object.keys(widgetStructureMap.value).indexOf(button.type) >= 0 && (!Object.getOwnPropertyDescriptor(button, 'hide') || (button.hide && !button.hide())),
    ));
    const availableGameWidgetsButtonsMap: ComputedRef<Array<WidgetButton>> = computed(() => allGameWidgetsMap.filter(
      (button) => Object.keys(widgetStructureMap.value).indexOf(button.type) >= 0 && (!Object.getOwnPropertyDescriptor(button, 'hide') || (button.hide && !button.hide())),
    ));

    const showStructureWidgets = computed(() => availableStructureWidgetsButtonsMap.value.length > 0 && (isDisplayTemplate(templateType.value) || isEmailTemplate(templateType.value)));
    const showFormWidgets = computed(() => availableFormWidgetsButtonsMap.value.length > 0 && isDisplayTemplate(templateType.value));

    const searchQuery = ref('');

    // Use global state so accordion state persists across panel open/close
    const collapsedSections = computed(() => state.widgetsPanelCollapsedSections);

    const toggleSection = (section: string) => {
      toggleWidgetsPanelSection(section);
    };

    const getButtonLabel = (button: WidgetButton): string => {
      return typeof button.label === 'function' ? button.label() : button.label;
    };

    const matchesSearch = (label: string): boolean => {
      if (!searchQuery.value) return true;
      return label.toLowerCase().includes(searchQuery.value.toLowerCase());
    };

    const filteredWidgetsButtonsMap = computed(() =>
      availableWidgetsButtonsMap.value.filter((b) => matchesSearch(getButtonLabel(b))),
    );
    const filteredStructureWidgetsButtonsMap = computed(() =>
      availableStructureWidgetsButtonsMap.value.filter((b) => matchesSearch(getButtonLabel(b))),
    );
    const filteredFormWidgetsButtonsMap = computed(() =>
      availableFormWidgetsButtonsMap.value.filter((b) => matchesSearch(getButtonLabel(b))),
    );
    const filteredGameWidgetsButtonsMap = computed(() =>
      availableGameWidgetsButtonsMap.value.filter((b) => matchesSearch(getButtonLabel(b))),
    );

    // --- Integration widgets (declarative, from the catalogue) ---
    const { locale: appWidgetLocale } = useI18n();
    const {
      fetchCatalog: fetchWidgetCatalog,
      resolveIntegrationPlaceholders,
    } = useIntegrationWidgets();
    const widgetCatalog = ref<WidgetCatalogEntry[]>([]);

    // Sélection de texte localisé via l'utilitaire central (G12).
    const localizedWidgetText = (text?: WidgetLocalizedText): string =>
      pickLocalizedText(text as any, String(appWidgetLocale.value || 'fr'));

    // D4 — « mise à jour disponible » : compare la version FIGÉE au drop
    // (data-widget-version) de chaque widget d'intégration POSÉ à la version
    // courante du catalogue (déclaration.version, repli latest_version). Best-effort,
    // recalculé au (re)chargement du catalogue. Ne bloque jamais l'édition.
    const outdatedIntegrationWidgets = ref<
      Array<{ slug: string; key: string; from: number; to: number }>
    >([]);
    const refreshOutdatedIntegrationWidgets = (): void => {
      try {
        const doc = getTemplateIframeDocument();
        if (!doc) {
          outdatedIntegrationWidgets.value = [];
          return;
        }
        const nodes = Array.from(
          doc.querySelectorAll('[data-widgettype="spm_widget_integration"]'),
        ) as HTMLElement[];
        const out: Array<{ slug: string; key: string; from: number; to: number }> = [];
        nodes.forEach((el) => {
          const slug = el.getAttribute('data-integration-slug') || '';
          const key = el.getAttribute('data-widget-key') || '';
          if (!slug || !key) return;
          const snap = Number(el.getAttribute('data-widget-version')) || 1;
          const entry = widgetCatalog.value.find((e) => e.integration_slug === slug);
          const declaration = entry?.widgets.find((w) => w.key === key);
          const declVersion = Number(declaration?.version);
          const entryLatest = Number(entry?.latest_version);
          const current = declVersion > 0 ? declVersion : entryLatest > 0 ? entryLatest : 1;
          if (current > snap) out.push({ slug, key, from: snap, to: current });
        });
        outdatedIntegrationWidgets.value = out;
      } catch {
        outdatedIntegrationWidgets.value = [];
      }
    };

    /**
     * Build the HTML for a dropped integration widget from the catalogue cache:
     * resolve {integration.*} into the URL template, seed {widget.*} defaults
     * from the config_schema, and render the self-contained block.
     */
    const buildIntegrationWidgetHtml = (slug: string, key: string): string => {
      const entry = widgetCatalog.value.find((e) => e.integration_slug === slug);
      const declaration = entry?.widgets.find((w) => w.key === key);
      if (!entry || !declaration) return '';

      const config: Record<string, unknown> = {};
      const schemaFields = [
        ...(declaration.config_schema?.fields || []),
        ...((declaration.config_schema?.groups || []).flatMap((g) => g.fields || [])),
        ...((declaration.config_schema?.style_groups || []).flatMap((g) => g.fields || [])),
      ];
      schemaFields.forEach((f) => {
        if (f.default !== undefined) config[f.key] = f.default;
      });

      const urlTemplate = resolveIntegrationPlaceholders(
        declaration.image_url_template || '',
        entry.integration_config_values || {},
      );

      return rawWidgetIntegration({
        slug,
        key,
        name: localizedWidgetText(declaration.name),
        urlTemplate,
        config,
        defaultWidth: declaration.default_width,
        schema: declaration.config_schema,
        renderType: declaration.render_type,
        renderMode: declaration.render_mode,
        target: widgetTarget.value ?? 'email_template',
        integrationName: entry.integration_name,
        version: declaration.version,
      });
    };

    // Drop path: read slug/key off the dragged side-panel item.
    const buildIntegrationWidgetHtmlFromDrop = (item: HTMLElement): string =>
      buildIntegrationWidgetHtml(
        item.getAttribute('data-integration-slug') || '',
        item.getAttribute('data-widget-key') || '',
      );

    // G2 — filet de récupération quand un drop de widget d'intégration échoue
    // faute de catalogue chargé : on force un re-fetch (contourne le cache) puis
    // on informe l'utilisateur. Le drop lui-même est déjà annulé proprement par
    // l'appelant ; ici on ré-hydrate le catalogue pour que le prochain glisser
    // fonctionne, et on ne laisse jamais l'échec passer inaperçu (toast).
    const recoverIntegrationWidgetDrop = () => {
      const target = widgetTarget.value;
      if (target) {
        fetchWidgetCatalog(target, { force: true })
          .then((data) => { widgetCatalog.value = data; refreshOutdatedIntegrationWidgets(); })
          .catch(() => { /* fetchCatalog gère déjà l'échec en interne */ });
      }
      showToast('warn', 'templateBuilder.panels.widgets.dropCatalogNotReady');
    };

    const allAppWidgets = computed<AppWidget[]>(() => {
      const widgets: AppWidget[] = [];

      // Declarative integration widgets (image + html) for the current target
      // (email_template / popup / smart_content).
      if (widgetTarget.value) {
        widgetCatalog.value.forEach((entry) => {
          entry.widgets.forEach((w) => {
            widgets.push({
              id: `app_int_${entry.integration_slug}_${w.key}`,
              type: WidgetTypeEnum.INTEGRATION_WIDGET,
              group: TemplateStructureEnum.WIDGET,
              label: localizedWidgetText(w.name) || w.key,
              appName: entry.integration_name,
              iconImage: (w.icon_url ?? entry.integration_icon_url) ?? undefined,
              installed: entry.shop_integration_status === 'active',
              marketplaceUrl: entry.marketplace_url,
              integrationSlug: entry.integration_slug,
              widgetKey: w.key,
              kind: entry.kind,
            });
          });
        });
      }

      return widgets;
    });

    // First-party (kind: shopimind) widgets are listed WITH the native widgets;
    // only partner widgets stay in the "Applications" marketplace section.
    const appWidgets = computed<AppWidget[]>(() =>
      allAppWidgets.value.filter((w) => w.kind !== 'shopimind'),
    );
    const shopimindWidgets = computed<AppWidget[]>(() =>
      allAppWidgets.value.filter((w) => w.kind === 'shopimind'),
    );

    const filteredAppWidgets = computed(() =>
      appWidgets.value.filter((w) => matchesSearch(w.label) || matchesSearch(w.appName)),
    );
    const filteredShopimindWidgets = computed(() =>
      shopimindWidgets.value.filter((w) => matchesSearch(w.label) || matchesSearch(w.appName)),
    );

    const hasNoResults = computed(() => {
      if (!searchQuery.value) return false;
      return filteredWidgetsButtonsMap.value.length === 0
        && filteredStructureWidgetsButtonsMap.value.length === 0
        && filteredFormWidgetsButtonsMap.value.length === 0
        && filteredGameWidgetsButtonsMap.value.length === 0
        && filteredShopimindWidgets.value.length === 0
        && filteredAppWidgets.value.length === 0;
    });

    const handleAppInstall = (appWidget: AppWidget) => {
      // Cul-de-sac de gating : marketplace ShopiMind masquée → « Bientôt disponible »
      // (aucune ouverture d'onglet vers une page indisponible).
      if (!MARKETPLACE_ENABLED) {
        showToast('info', 'integrations.comingSoon.subtitle');
        return;
      }
      if (appWidget.marketplaceUrl) {
        window.open(appWidget.marketplaceUrl, '_blank');
      }
    };

    // Integration widgets browser modal (in-editor "marketplace"). Click-to-add:
    // a centered modal can't host the native drag-into-iframe drop (it covers the
    // canvas), so cards are added by click — reusing the same HTML builder and
    // the same post-insert steps as a drop — into the selected/last column.
    const showWidgetsModal = ref(false);

    const handleViewAllApps = () => {
      showWidgetsModal.value = true;
    };
    const handleOpenMarketplace = () => {
      if (!MARKETPLACE_ENABLED) {
        showToast('info', 'integrations.comingSoon.subtitle');
        return;
      }
      window.open('/shop/integrations', '_blank');
    };
    const handleModalInstall = (entry: WidgetCatalogEntry) => {
      if (!MARKETPLACE_ENABLED) {
        showToast('info', 'integrations.comingSoon.subtitle');
        return;
      }
      window.open(entry?.marketplace_url || '/shop/integrations', '_blank');
    };

    // --- Click-to-place: pick a widget in the modal, then click in the canvas
    // to choose exactly where it lands. A highlighted insertion line follows the
    // cursor; Esc / the banner cancels. Reuses the same insertion + init steps as
    // a drop. (A centered modal can't host the native drag-into-iframe drop, so
    // we offer precise placement by click instead.) ---
    /* eslint-disable no-use-before-define */
    const placementMode = ref(false);
    let placementWidget: { slug: string; key: string } | null = null;
    let placementIndicator: HTMLElement | null = null;
    let placementTarget: { droppable: HTMLElement; refNode: HTMLElement | null } | null = null;

    function insertIntegrationWidgetAt(
      slug: string,
      key: string,
      droppable: HTMLElement,
      refNode: HTMLElement | null,
    ): void {
      const doc = getTemplateIframeDocument();
      if (!doc) return;
      const rawHtml = buildIntegrationWidgetHtml(slug, key);
      if (!rawHtml) return;

      const savePointPromise = checkIfSavePointNeeded();
      const { newHtml } = generateTranslations(rawHtml, {
        newElement: true,
        translateVariables: true,
      });

      doc.querySelector('body')?.classList.add('iframe-locked');
      if (refNode && refNode.parentElement === droppable) {
        refNode.insertAdjacentHTML('beforebegin', newHtml as string);
      } else {
        droppable.insertAdjacentHTML('beforeend', newHtml as string);
      }

      const typeMatch = /data-widgettype="([^"]+)"/.exec(newHtml as string);
      if (typeMatch?.[1]) injectWidgetCss(typeMatch[1]);

      // Reference + initialise the freshly inserted widget (renders the image /
      // html) exactly like the drop path (createWidgetSortable.onEnd → initWidget).
      const idMatch = /id="(spm_widget_[0-9a-zA-Z]+)"/.exec(newHtml as string);
      const newEl = idMatch ? doc.getElementById(idMatch[1]) : null;

      const sectionParent = getSectionParent(droppable);
      const sid = sectionParent?.getAttribute('data-spmelementid');
      if (sid) setActiveSection(parseInt(sid, 10));

      setTimeout(() => {
        doc.querySelector('body')?.classList.remove('iframe-locked');
      }, 2000);

      resetActiveSection();
      resetListeners();
      if (newEl) {
        initWidget({
          trigger: 'drop',
          widget: newEl,
          templateType: getTemplateParentTypeByType(templateType.value),
        });
        setTimeout(() => newEl.scrollIntoView({ block: 'center' }), 120);
      }

      setTimeout(() => {
        savePointPromise.then(() => {
          updateSectionsInState(false);
          createHistory(HistoryType.ADD_WIDGET);
        });
      }, 0);

      markTemplateAsEdited();
    }

    function computePlacementRef(droppable: HTMLElement, clientY: number): HTMLElement | null {
      const children = Array.from(droppable.children).filter(
        (c) => c !== placementIndicator && c.nodeType === 1,
      ) as HTMLElement[];
      for (let i = 0; i < children.length; i += 1) {
        const rect = children[i].getBoundingClientRect();
        if (clientY < rect.top + rect.height / 2) return children[i];
      }
      return null;
    }

    function endPlacementMode(): void {
      const doc = getTemplateIframeDocument();
      if (doc) {
        doc.body.classList.remove('spm_placement_mode');
        doc
          .querySelectorAll('.spm_placement_hover')
          .forEach((el) => el.classList.remove('spm_placement_hover'));
        doc.removeEventListener('mousemove', onPlacementMove, true);
        doc.removeEventListener('click', onPlacementClick, true);
        doc.removeEventListener('keydown', onPlacementKey, true);
      }
      document.removeEventListener('keydown', onPlacementKey, true);
      if (placementIndicator?.parentElement) placementIndicator.remove();
      placementIndicator = null;
      placementTarget = null;
      placementWidget = null;
      placementMode.value = false;
    }

    function onPlacementMove(e: Event): void {
      const doc = getTemplateIframeDocument();
      if (!doc || !placementIndicator) return;
      const droppable = (e.target as HTMLElement)?.closest(
        WIDGET_DROPPABLE_CLASS,
      ) as HTMLElement | null;
      doc
        .querySelectorAll('.spm_placement_hover')
        .forEach((el) => el.classList.remove('spm_placement_hover'));
      if (!droppable) {
        if (placementIndicator.parentElement) placementIndicator.remove();
        placementTarget = null;
        return;
      }
      droppable.classList.add('spm_placement_hover');
      const refNode = computePlacementRef(droppable, (e as MouseEvent).clientY);
      if (refNode) droppable.insertBefore(placementIndicator, refNode);
      else droppable.appendChild(placementIndicator);
      placementTarget = { droppable, refNode };
    }

    function onPlacementClick(e: Event): void {
      e.preventDefault();
      e.stopPropagation();
      const target = placementTarget;
      const widget = placementWidget;
      endPlacementMode();
      if (target && widget) {
        insertIntegrationWidgetAt(widget.slug, widget.key, target.droppable, target.refNode);
      }
    }

    function onPlacementKey(e: Event): void {
      if ((e as KeyboardEvent).key === 'Escape') endPlacementMode();
    }

    function startPlacementMode(slug: string, key: string): void {
      const doc = getTemplateIframeDocument();
      if (!doc) return;
      placementWidget = { slug, key };
      showWidgetsModal.value = false;

      // We don't add BUILDER_DRAG_ACTIVE_CLASS — our own hover outline + insertion
      // line (and the native column :hover reveal) are enough to guide placement.
      doc.body.classList.add('spm_placement_mode');
      placementIndicator = doc.createElement('div');
      placementIndicator.className = 'spm_placement_indicator';

      doc.addEventListener('mousemove', onPlacementMove, true);
      doc.addEventListener('click', onPlacementClick, true);
      doc.addEventListener('keydown', onPlacementKey, true);
      document.addEventListener('keydown', onPlacementKey, true);

      placementMode.value = true;
    }
    /* eslint-enable no-use-before-define */

    const handleAddWidgetFromModal = (payload: { slug: string; key: string }) => {
      startPlacementMode(payload.slug, payload.key);
    };

    const formWidgetShowLabel = computed(() => store.getters['liveEditor/getFormWidgetShowLabel']);

    watch(formWidgetShowLabel, (newVal) => {
      additionalWidgetsConfiguration.value.showLabel = newVal;
    });

    onBeforeMount(async () => {
      // Get active social networks to build widgets HTML
      const activeNetworks = await GetShopsConfigurationList({
        shopId: UserState.activeShop?.id ?? 0,
        key: 'shopRequiredDataSocialNetworks_social_network',
        fields: 'value',
        limit: 0,
        offset: 0,
        lang: '',
      });

      if (activeNetworks && activeNetworks.length > 0) {
        const [networks] = activeNetworks;
        const value = unserialize(networks.value);

        if (value.length > 0 && value.indexOf('contactPage') !== -1) {
          // Replace contactPage by email
          value.push('email');
          value.splice(value.indexOf('contactPage'), 1);
        }

        additionalWidgetsConfiguration.value.socialNetworks = value;
      }

      // Load the integration widgets catalogue for the current editor target
      // (email → email_template, popup → popup, embed → smart_content). The
      // app-widgets sortable is (re)created reactively when this populates.
      if (widgetTarget.value) {
        widgetCatalog.value = await fetchWidgetCatalog(widgetTarget.value);
        // D4 — le canevas est monté à ce stade : détecte les widgets posés obsolètes.
        refreshOutdatedIntegrationWidgets();
      }
    });

    const createWidgetSortable = (selector: string): Sortable | null => {
      const panel = document.querySelector(selector) as HTMLElement;
      if (panel) {
        const sortable = Sortable.create(panel, {
          group: { name: TemplateStructureEnum.WIDGET, pull: 'clone', put: false },
          sort: false,
          animation: ANIMATION_SPEED,
          filter: '.widget-btn--disabled', // disable drag for disabled widgets
          onChoose(evt) {
            const featurePermission = hasAccessToFeatures(evt.item.getAttribute('data-widget-type'));
            if (featurePermission && !featurePermission.access) {
              store.commit('general/setIsFeatureUnavailableModalVisible', true);
              store.commit('general/setFeatureUnavailableMinPlan', featurePermission.minimumPlan);
              return false;
            }
            const { item } = evt;

            // Listen to `dragover` to track mouse coordinates
            document.removeEventListener('dragover', trackMousePosition);
            document.addEventListener('dragover', trackMousePosition, { passive: true });

            // Attach drag event
            item.addEventListener('drag', handleDrag, { capture: true });

            // Reveal empty column placeholders during drag
            getTemplateIframeDocument()?.body?.classList.add(BUILDER_DRAG_ACTIVE_CLASS);

            return true;
          },
          onMove(evt) {
            stopScrolling();

            clearPlaceholder();

            const target = evt.related;
            if (target.classList.contains(WIDGETS_PLACEHOLDER_IDENTIFIER)) {
              const parent = target.parentElement;
              if (parent) {
                parent.classList.add(BUILDER_SORTABLE_PLACEHOLDER_HOVER_CLASS);
              }
            }
            const section = getSectionParent(target);
            if (section) {
              section.classList.add(BUILDER_ELEMENT_HOVER_CLASS);
            }
          },
          onUnchoose() {
            clearPlaceholder();
            getTemplateIframeDocument()?.body?.classList.remove(BUILDER_DRAG_ACTIVE_CLASS);
          },
          onEnd(evt) {
            const template = getTemplateIframeDocument();

            const { item } = evt;
            item.removeEventListener('drag', handleDrag);

            document.removeEventListener('dragover', trackMousePosition);

            stopScrolling();

            // Remove drag active class to hide empty column placeholders
            template?.body?.classList.remove(BUILDER_DRAG_ACTIVE_CLASS);

            if (evt.from !== evt.to) {
              const insertWidget = () => {
                // Fire-and-forget: start save point check now (captures pre-change state synchronously)
                const savePointPromise = checkIfSavePointNeeded();

                // IMMEDIATE visual operations (no async wait)

                // Lock click on widgets and sections before listeners are applied
                // eslint-disable-next-line no-unused-expressions
                template.querySelector('body')?.classList.add('iframe-locked');

                // Remove hover class
                clearPlaceholder();

                const destinationColumn = evt.to;

                // If destination column contains placeholder class, we remove it
                if (destinationColumn.classList.contains(COLUMN_WITH_PLACEHOLDER_CLASS)) {
                  // Remove class and placeholder
                  destinationColumn.classList.remove(COLUMN_WITH_PLACEHOLDER_CLASS);

                  // If destination column contains a placeholder for widget, we remove it
                  if (destinationColumn.querySelector(`.${WIDGETS_PLACEHOLDER_IDENTIFIER}`)) {
                    // eslint-disable-next-line no-unused-expressions
                    destinationColumn.querySelector(`.${WIDGETS_PLACEHOLDER_IDENTIFIER}`)?.remove();
                  }
                }

                // We get the HTML code of the selected widget
                const widgetType = evt.item.getAttribute('data-widget-type');

                if (widgetType) {
                  // Mark section of parent column as active
                  const column = getParents(evt.item, WIDGET_DROPPABLE_CLASS);

                  if (column) {
                    setActiveSection(computeActiveSection(TemplateStructureEnum.COLUMN, column[0].id));

                    let elementHtml: string;
                    if (widgetType === WidgetTypeEnum.INTEGRATION_WIDGET) {
                      // Built from the catalogue cache (declaration + config) —
                      // not from the static widgetStructureMap.
                      elementHtml = buildIntegrationWidgetHtmlFromDrop(evt.item as HTMLElement);
                      // G2 — drop fiable : si le catalogue n'était pas (encore) chargé
                      // ou ne contient pas la déclaration, `elementHtml` est vide. On
                      // ne perd JAMAIS le drop silencieusement : on retire l'item
                      // fantôme, on relance un fetch forcé du catalogue et on prévient
                      // par un toast (l'utilisateur pourra re-glisser une fois chargé).
                      if (!elementHtml) {
                        // eslint-disable-next-line no-unused-expressions
                        evt.item?.remove();
                        // Le drop est annulé : RELÂCHER le verrou d'itération posé
                        // par insertWidget (sinon le canvas reste figé —
                        // .iframe-locked applique pointer-events:none aux sections
                        // et widgets, et aucun unlock passif ne se déclenche ici).
                        getTemplateIframeDocument()
                          ?.querySelector('body')
                          ?.classList.remove('iframe-locked');
                        recoverIntegrationWidgetDrop();
                        return;
                      }
                    } else {
                      elementHtml = widgetStructureMap.value[widgetType]();
                    }

                    let translateVariables = true;
                    const forceTranslateHiddenElement = allFormWidgetsMap.map((formWidget) => formWidget.type).includes(widgetType as WidgetTypeEnum);

                    if (widgetType === WidgetTypeEnum.SMART_PRODUCT_LIST) {
                      translateVariables = false;
                    }

                    if (widgetType === WidgetTypeEnum.IMPORT_ZIP) {
                      const elementId = new RegExp('id="(spm_widget_[0-9a-zA-Z]+)"').exec(elementHtml);
                      if (elementId && elementId.length > 1) {
                        store.commit('liveEditor/setIsChooseZipFileModalVisible', true);
                        store.commit('liveEditor/setSelectedWidgetId', elementId[1]);
                        store.commit('liveEditor/setSelectedWidget', (html: string) => {
                          // eslint-disable-next-line no-param-reassign
                          evt.item.outerHTML = elementHtml.replace(WIDGET_IMPORT_ZIP_PLACEHOLDER_IDENTIFIER, html);
                          // Inject widget-specific static CSS if defined in registry
                          const zipWidgetTypeMatch = /data-widgettype="([^"]+)"/.exec(elementHtml);
                          if (zipWidgetTypeMatch && zipWidgetTypeMatch[1]) {
                            injectWidgetCss(zipWidgetTypeMatch[1]);
                          }
                        });
                      }
                    } else {
                      // Create translations for new widget
                      const { newHtml } = generateTranslations(
                        elementHtml,
                        {
                          newElement: true,
                          translateVariables,
                          forceTranslateHiddenElement,
                        },
                      );

                      // IMMEDIATE: Replace the dragged panel item with the actual widget HTML
                      // eslint-disable-next-line no-param-reassign
                      evt.item.outerHTML = newHtml as string;

                      // Inject widget-specific static CSS if defined in registry
                      const dataWidgetTypeMatch = /data-widgettype="([^"]+)"/.exec(elementHtml);
                      if (dataWidgetTypeMatch && dataWidgetTypeMatch[1]) {
                        injectWidgetCss(dataWidgetTypeMatch[1]);
                      }

                      // Unlock iframe after a few seconds in case of something went wrong
                      setTimeout(() => {
                        // eslint-disable-next-line no-unused-expressions
                        template.querySelector('body')?.classList.remove('iframe-locked');
                      }, 2000);

                      if (widgetType === WidgetTypeEnum.IMAGE) {
                        const elementId = new RegExp('id="(spm_widget_[0-9a-zA-Z]+)"').exec(elementHtml);

                        if (elementId && elementId.length > 0) {
                          // For widget image, we need to define the max-width property according to image size and parent size
                          const element = template.getElementById(elementId[1])?.querySelector('img');
                          if (element) {
                            // Set no src to remove image and calculate parent's width
                            const src = element.getAttribute('src');

                            if (src) {
                              element.setAttribute('src', '');

                              const selectorParent = isDisplayTemplate(templateType.value) ? '.spm_widget_display_image' : '.spm_widget_image';
                              const parent = getParents(element, selectorParent);

                              if (parent && parent.length) {
                                // We set the correct src of the image
                                element.setAttribute('src', src);
                                // eslint-disable-next-line no-param-reassign
                                element.onload = () => {
                                  // When image is loaded, we calculate its width
                                  const image = element as HTMLImageElement;
                                  if (image) {
                                    if (isEmailTemplate(state.template?.type)) {
                                      setPerfectSizeForImage(image, '100%');
                                    } else {
                                      addOrUpdateDynamicStyle(`#${elementId[1]} img`, { 'max-width': '100%' });
                                      image.setAttribute('width', '100%');
                                    }
                                  }

                                  // Reset listeners immediately so widget is decorated and draggable
                                  resetActiveSection();
                                  resetListeners();

                                  // Defer heavy work to after browser repaint, chained on savePointPromise
                                  setTimeout(() => {
                                    savePointPromise.then(() => {
                                      executeWhenDragComplete(() => {
                                        updateSectionsInState(false);

                                        if (!selectedSyncElement.value) {
                                          createHistory(HistoryType.ADD_WIDGET);
                                        }
                                      });
                                    });
                                  }, 0);
                                  // Cancel onload event (The onload is called again when the fixImagesWidthBeforeSave method is called)
                                  element.onload = null;
                                };
                              }
                            }
                          }
                        }
                      } else {
                        // Reset listeners immediately so widget is decorated and draggable
                        resetActiveSection();
                        resetListeners();

                        // Defer heavy work to after browser repaint, chained on savePointPromise
                        setTimeout(() => {
                          savePointPromise.then(() => {
                            executeWhenDragComplete(() => {
                              updateSectionsInState(false);

                              if (!selectedSyncElement.value) {
                                createHistory(HistoryType.ADD_WIDGET);
                              }

                              // Refresh smart list if needed
                              if (widgetType === WidgetTypeEnum.SMART_PRODUCT_LIST) {
                                refreshSmartProductList();
                              }

                              // Initialize game funnel steps when a game widget is added
                              if (
                                widgetType === WidgetTypeEnum.GAME_WHEEL
                                || widgetType === WidgetTypeEnum.GAME_SLOT_MACHINE
                                || widgetType === WidgetTypeEnum.GAME_SCRATCH_CARD
                              ) {
                                syncGameFunnelSteps();
                              }

                              // Auto-update disclosure channels when email, phone or consent checkbox widget is added
                              if (widgetType === WidgetTypeEnum.INPUT_EMAIL || widgetType === WidgetTypeEnum.INPUT_PHONE || widgetType === WidgetTypeEnum.CONSENT_CHECKBOX) {
                                setTimeout(() => {
                                  updateDisclosureAfterWidgetAddition();
                                }, 150);
                              }

                              // Auto-select disclosure channels based on present form fields
                              if (widgetType === WidgetTypeEnum.DISCLOSURE) {
                                const disclosureIdMatch = new RegExp('id="(spm_widget_[0-9a-zA-Z]+)"').exec(elementHtml);
                                if (disclosureIdMatch && disclosureIdMatch.length > 1) {
                                  setTimeout(() => {
                                    const autoChannels = getAutoDisclosureChannels();
                                    // Use setter to update channels + auto-update default message
                                    import('@/components/template-builder/setters/form').then(({ setDisclosureChannels: setChannels }) => {
                                      setChannels(`#${disclosureIdMatch[1]}`, {
                                        name: 'data-disclosure-channels',
                                        value: autoChannels,
                                        getter: () => {},
                                        setters: [],
                                      });
                                    });
                                  }, 150);
                                }
                              }

                              // Initialize widget via generic registry (countdown, enkore, etc.)
                              const widgetIdMatch = new RegExp('id="(spm_widget_[0-9a-zA-Z]+)"').exec(elementHtml);
                              if (widgetIdMatch && widgetIdMatch.length > 1) {
                                setTimeout(() => {
                                  const template = getTemplateIframeDocument();
                                  const widget = template?.getElementById(widgetIdMatch[1]);
                                  if (widget) {
                                    initWidget({ trigger: 'drop', widget: widget as HTMLElement, templateType: getTemplateParentTypeByType(templateType.value) });
                                  }
                                }, 100);
                              }

                            });
                          });
                        }, 0);
                      }
                    }

                    if (allFormWidgetsMap.map((formWidget) => formWidget.type).includes(widgetType as WidgetTypeEnum)) {
                      const elementId = new RegExp('id="(spm_widget_[0-9a-zA-Z]+)"').exec(elementHtml);
                      if (elementId && elementId.length) {
                        addNewForm(elementId[1], widgetType);
                      }
                    }

                    // Apply global margin to button align attribute for EMAIL templates containing table.button
                    const elementId = new RegExp('id="(spm_widget_[0-9a-zA-Z]+)"').exec(elementHtml);
                    if (elementId && elementId.length) {
                      import('@/components/template-builder/setters').then(({ applyGlobalMarginToButtonAlign, applyGlobalBorderRadiusToButton }) => {
                        applyGlobalMarginToButtonAlign(elementId[1]);
                        // Apply global border-radius VML to button if needed
                        applyGlobalBorderRadiusToButton(elementId[1]);
                      });
                    }
                  }
                }
              };

              // SYNC ELEMENT WARNING
              if (!selectedSyncElement.value) {
                const sectionParent = getSectionParent(evt.to);
                if (sectionParent) {
                  const elementSyncId = sectionParent.getAttribute(TEMPLATE_SYNC_ELEMENT_IDENTIFIER) && !sectionParent.getAttribute(TEMPLATE_PART_ELEMENT_PART_TYPE);
                  if (elementSyncId && isEmailTemplate(state.template?.type)) {
                    const acceptCallback = () => {
                      insertWidget();
                      togglePanel('configurationPanel', null).then(() => {
                        if (selectedSyncElement.value) {
                          store.dispatch('liveEditor/prepareStructureConfiguration', selectedSyncElement.value.structureConfiguration);
                        }
                      });
                    };
                    store.dispatch('liveEditor/showSyncElementWarning', {
                      elementId: sectionParent.getAttribute('id'),
                      elementType: TemplateStructureEnum.SECTION,
                      acceptCallback,
                      rejectCallback: () => { clearPlaceholder(true); },
                    });
                  } else {
                    insertWidget();
                    markTemplateAsEdited();
                  }
                } else {
                  insertWidget();
                  markTemplateAsEdited();
                }
              } else {
                const sectionParent = getSectionParent(evt.to);
                if (sectionParent) {
                  const sectionParentId = sectionParent.getAttribute('id');
                  if (sectionParentId === selectedSyncElement.value.elementId) {
                    insertWidget();
                    markTemplateAsEdited();
                    updateTemplatePreviewDisplay();
                  } else {
                    clearPlaceholder(true);
                  }
                }
              }
            } else {
              // eslint-disable-next-line no-unused-expressions
              template.querySelector('body')?.classList.remove('iframe-locked');
            }
          },
        });
        return sortable;
      }
      return null;
    };

    onMounted(() => {
      additionalWidgetsConfiguration.value.showLabel = formWidgetShowLabel.value;

      // Clean up orphaned global sortable instances from previous mount
      // (their DOM elements were removed when Vue unmounted this component).
      // We do NOT destroy in onBeforeUnmount to avoid killing sortables mid-drag.
      pruneOrphanGlobalSortableInstances();

      const widgetSortable = createWidgetSortable('#widgets-panel-1');
      if (widgetSortable) {
        sortableGlobalInstances.push(widgetSortable);
      }

      if (showFormWidgets.value) {
        const formWidgetSortable = createWidgetSortable('#form-widgets-panel-1');
        if (formWidgetSortable) {
          sortableGlobalInstances.push(formWidgetSortable);
        }
      }

      if (showGameWidgets.value) {
        const gameWidgetSortable = createWidgetSortable('#game-widgets-panel-1');
        if (gameWidgetSortable) {
          sortableGlobalInstances.push(gameWidgetSortable);
        }
      }

      // App widgets sortable. The catalogue loads async, so (re)create the
      // sortable when appWidgets transitions from empty → populated.
      const setupAppWidgetSortable = () => {
        const appWidgetSortable = createWidgetSortable('#app-widgets-panel-1');
        if (appWidgetSortable) {
          sortableGlobalInstances.push(appWidgetSortable);
        }
      };
      if (appWidgets.value.length > 0) {
        setupAppWidgetSortable();
      }
      watch(
        () => appWidgets.value.length,
        async (len, oldLen) => {
          if (len > 0 && !oldLen) {
            await nextTick();
            setupAppWidgetSortable();
          }
        },
      );

      const panel2 = document.querySelector('#line') as HTMLElement;

      if (panel2) {
        const sortable = Sortable.create(panel2, {
          group: { name: TemplateStructureEnum.LINE, pull: 'clone', put: false },
          sort: false,
          animation: ANIMATION_SPEED,
          filter: `.sortable-group-${TemplateStructureEnum.SECTION}`,
          onChoose(evt) {
            const { item } = evt;

            // Listen to `dragover` to track mouse coordinates
            document.removeEventListener('dragover', trackMousePosition);
            document.addEventListener('dragover', trackMousePosition, { passive: true });

            // Attach drag event
            item.addEventListener('drag', handleDrag, { capture: true });
          },
          onMove() {
            stopScrolling();
          },
          onEnd(evt) {
            const { item } = evt;
            item.removeEventListener('drag', handleDrag);

            document.removeEventListener('dragover', trackMousePosition);

            stopScrolling();

            if (evt.from !== evt.to) {
              applySyncSectionDropPolicy(evt, {
                acceptCallback: showColumnWidgetModal,
                rejectCallback: removeColumnWidgetPlaceholders,
              });
            }
          },
        });
        sortableGlobalInstances.push(sortable);
      }

      const panel3 = document.querySelector('#section') as HTMLElement;

      if (panel3) {
        const sortable = Sortable.create(panel3, {
          group: { name: TemplateStructureEnum.SECTION, pull: 'clone', put: false },
          sort: false,
          animation: ANIMATION_SPEED,
          filter: `.sortable-group-${TemplateStructureEnum.LINE}`,
          onChoose(evt) {
            const { item } = evt;

            // Listen to `dragover` to track mouse coordinates
            document.removeEventListener('dragover', trackMousePosition);
            document.addEventListener('dragover', trackMousePosition, { passive: true });

            // Attach drag event
            item.addEventListener('drag', handleDrag, { capture: true });
          },
          onMove() {
            stopScrolling();
          },
          onEnd(evt) {
            const { item } = evt;
            item.removeEventListener('drag', handleDrag);

            document.removeEventListener('dragover', trackMousePosition);

            stopScrolling();

            if (evt.from !== evt.to) {
              showElementsModal([TemplateStructureEnum.SECTION]);
            }
          },
        });
        sortableGlobalInstances.push(sortable);
      }
    });

    onBeforeUnmount(() => {
      // Tear down click-to-place listeners if a placement was in progress.
      endPlacementMode();
      // Do NOT call destroyAllSortables(true) here:
      // WidgetsPanel can remount during a drag operation (e.g. insertNewColumn → updateSectionsInState).
      // Destroying sortables mid-drag causes "el is null" crashes and stuck drag state.
      // Orphaned instances are cleaned up in onMounted via pruneOrphanGlobalSortableInstances().
      // Full cleanup happens in TemplateEditor.onBeforeUnmount via destroyAllSortables().
      allWidgetsMap.value = [];
      allStructureWidgetsMap.value = [];
    });

    return {
      t,
      filteredWidgetsButtonsMap,
      filteredStructureWidgetsButtonsMap,
      filteredFormWidgetsButtonsMap,
      filteredGameWidgetsButtonsMap,
      filteredAppWidgets,
      filteredShopimindWidgets,
      outdatedIntegrationWidgets,
      isEmbed,
      showStructureWidgets,
      showFormWidgets,
      showGameWidgets,
      BUILDER_ELEMENT_PLACEHOLDER,
      hideLeftToolbar,
      searchQuery,
      collapsedSections,
      toggleSection,
      hasNoResults,
      handleAppInstall,
      handleViewAllApps,
      handleOpenMarketplace,
      handleModalInstall,
      handleAddWidgetFromModal,
      placementMode,
      endPlacementMode,
      showWidgetsModal,
      widgetCatalog,
      appWidgetLocale,
      marketplaceEnabled: MARKETPLACE_ENABLED,
    };
  },
});
